import { QuantitySelectorComponent } from '@theme/component-quantity-selector';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * Gang sheets are sold per decimetre, so the cart line quantity is
 * "decimetres per sheet × number of sheets". Customers think in sheets, so this
 * selector counts sheets and converts back to decimetres when it tells the cart
 * what the new line quantity should be.
 *
 * @extends {QuantitySelectorComponent}
 */
class SheetQuantitySelectorComponent extends QuantitySelectorComponent {
  get unit() {
    return Number(this.dataset.unit) || 1;
  }

  getEffectiveMax() {
    return this.getCurrentValues().max;
  }

  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    minusButton.disabled = value <= min;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }

  onQuantityChange() {
    const { quantityInput } = this.refs;
    const sheets = parseInt(quantityInput.value) || 1;
    const quantity = sheets * this.unit;

    // What the cart form submits for this line at checkout: the line's real
    // quantity, never the sheet count shown in the input.
    const line = this.querySelector('input[data-tc-line-quantity]');
    if (line instanceof HTMLInputElement) line.value = String(quantity);

    this.dispatchEvent(new QuantitySelectorUpdateEvent(quantity, Number(quantityInput.dataset.cartLine) || undefined));
  }
}

if (!customElements.get('tc-sheet-quantity-selector-component')) {
  customElements.define('tc-sheet-quantity-selector-component', SheetQuantitySelectorComponent);
}

/**
 * Cart lines the old cart form left at the sheet count. Until 2026-10-04
 * the checkout button posted the sheet count as each gang sheet line's
 * quantity, so a 2,2 m sheet (22 dm) stayed in the cart as 1 dm, at the
 * price of one decimetre. The cart marks such lines with the quantity they
 * should have (their sheet count × decimetres per sheet); put them back
 * once and reload, so the cart shows, and checkout charges, the real length.
 */
async function repairSheetLines() {
  const marks = document.querySelectorAll('[data-tc-repair-key]');
  if (marks.length === 0) return;

  /** @type {Record<string, number>} */
  const updates = {};
  for (const mark of marks) {
    if (!(mark instanceof HTMLElement)) continue;
    const quantity = Number(mark.dataset.tcRepairQuantity);
    if (mark.dataset.tcRepairKey && quantity > 0) updates[mark.dataset.tcRepairKey] = quantity;
  }
  if (Object.keys(updates).length === 0) return;

  // Once per repair and session: a cart that will not take the quantity
  // must not reload over and over.
  const attempt = JSON.stringify(updates);
  try {
    if (sessionStorage.getItem('tc-sheet-repair') === attempt) return;
    sessionStorage.setItem('tc-sheet-repair', attempt);
  } catch {
    // Without storage the check below still stops a loop.
  }

  try {
    const url = `${window.Theme?.routes?.cart_update_url || '/cart/update'}.js`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });
    if (!response.ok) return;
    const cart = await response.json();
    const fixed = Object.entries(updates).every(([key, quantity]) =>
      (cart.items || []).some((/** @type {{key: string, quantity: number}} */ item) => item.key === key && item.quantity === quantity)
    );
    if (fixed) window.location.reload();
  } catch (error) {
    console.error('Could not restore the length of a gang sheet in the cart:', error);
  }
}

repairSheetLines();
